:host {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
  overscroll-behavior: none;
  font-family: var(--typography-font-family-ui, system-ui);
  --ds-shell-panel-tools-width: var(--dimension-panel-width-xs);
}

:host {
  --_shell-chrome-bg: var(--color-background-secondary);
}

.shell-app__row {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  position: relative;
}

.shell-app__banner {
  flex: 0 0 auto;
  min-width: 0;
  position: relative;
  z-index: 5;
}

.shell-app__banner ::slotted(ds-banner) {
  display: grid;
  width: 100%;
}

/*
 * One chrome layer behind the shell row. Wash uses background-attachment: fixed
 * so the pattern is locked to the viewport — panel/tools collapse only changes the
 * transparent window, not the pattern phase. JS sets wash background-size from
 * readShellViewportDimensions(), not the shell element box.
 */
.shell-app__chrome {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  background-color: var(--_shell-chrome-bg);
}

:host(.shell-app--gradient) .shell-app__chrome::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-position: 0 0;
  background-attachment: fixed;
  z-index: 1;
  opacity: var(--ds-shell-gradient-opacity, 1);
  background-image: var(--ds-shell-gradient-image, none);
  background-size: var(--ds-shell-gradient-size, auto);
  background-repeat: no-repeat;
}

.shell-app__paper-texture {
  z-index: 2;
}

.shell-app__panel {
  flex: 0 0 auto;
  min-height: 0;
  height: 100%;
  position: relative;
  /* Above main so resize handle line can paint over the workspace edge */
  z-index: 2;
}

.shell-app__panel ::slotted(ds-panel-nav) {
  display: block;
  height: 100%;
}

.shell-app__panel > ds-panel-nav {
  display: block;
  height: 100%;
}

.shell-app__main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-areas:
    'bar tools'
    'content tools';
  position: relative;
  z-index: 1;
}

.shell-app__mobile-sheet-nav,
.shell-app__mobile-bar-nav {
  display: none;
}

.shell-app__bar {
  grid-area: bar;
  flex: 0 0 auto;
  min-width: 0;
}

.shell-app__bar ::slotted(ds-bar-nav) {
  display: block;
}

.shell-app__bar > ds-bar-nav {
  display: block;
}

.shell-app__content {
  grid-area: content;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: none;
  /* Limit reflow to the page column — not on __main (breaks tools flex shrink). */
  contain: layout;
}

/* Layout scroller, not a control: keep it keyboard-focusable without a page-sized ring. */
.shell-app__content:focus,
.shell-app__content:focus-visible {
  outline: none;
}

/* Opaque page surface — masks the fixed chrome layer in the content column. */
.shell-app__content {
  position: relative;
  z-index: 1;
  background-color: var(--color-background-primary);
}

.shell-app__tools {
  grid-area: tools;
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  position: relative;
  z-index: 0;
  overflow: hidden;
}

.shell-app__tools ::slotted(ds-panel-tools) {
  display: block;
  height: 100%;
  min-height: 0;
}

.shell-app__tools ::slotted(ds-shell-tools) {
  display: block;
  height: 100%;
  min-height: 0;
}

.shell-app__tools > ds-shell-tools {
  display: block;
  height: 100%;
  min-height: 0;
}

.shell-app__tool-slot-proxy {
  display: block;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.shell-app__content > ds-shell-page {
  display: block;
  min-height: 100%;
}

:host(.shell-app--tools-fullscreen) .shell-app__panel,
:host(.shell-app--tools-fullscreen) .shell-app__bar,
:host(.shell-app--tools-fullscreen) .shell-app__content {
  visibility: hidden;
}

:host(.shell-app--mobile) {
  --_shell-chrome-bg: var(--color-background-primary);
  padding-block-start: env(safe-area-inset-top);
}

/* Refresh the Mobile WebKit stage layer without remounting app content. */
:host(.shell-app--mobile.shell-app--foreground-refresh) .shell-app__main {
  transform: translateZ(0);
}

:host(.shell-app--mobile) .shell-app__chrome,
:host(.shell-app--mobile) .shell-app__panel,
:host(.shell-app--mobile) .shell-app__bar {
  display: none;
}

:host(.shell-app--mobile) .shell-app__main {
  width: 100%;
  box-sizing: border-box;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) auto;
  grid-template-areas:
    'stage'
    'mobile-bar-nav';
  background: var(--color-background-primary);
}

:host(.shell-app--mobile) .shell-app__content,
:host(.shell-app--mobile) .shell-app__tools,
:host(.shell-app--mobile) .shell-app__mobile-sheet-nav {
  grid-area: stage;
  min-width: 0;
  min-height: 0;
  height: 100%;
  position: relative;
}

:host(.shell-app--mobile) .shell-app__content {
  z-index: 1;
  overflow: auto;
}

:host(.shell-app--mobile) .shell-app__tools {
  z-index: 2;
  width: 100%;
  overflow: hidden;
  background: var(--color-background-primary);
}

:host(.shell-app--mobile) .shell-app__mobile-sheet-nav {
  display: block;
  z-index: 3;
  overflow: hidden;
  background: var(--color-background-primary);
}

:host(.shell-app--mobile) .shell-app__mobile-bar-nav {
  grid-area: mobile-bar-nav;
  display: block;
  min-width: 0;
  position: relative;
  z-index: 4;
}

:host(.shell-app--mobile) .shell-app__tools[hidden],
:host(.shell-app--mobile) .shell-app__mobile-sheet-nav[hidden],
:host(.shell-app--mobile) .shell-app__mobile-bar-nav[hidden] {
  display: none;
}

:host(.shell-app--mobile-tool-active) .shell-app__content,
:host(.shell-app--mobile-sheet-nav-open) .shell-app__content,
:host(.shell-app--mobile-sheet-nav-open) .shell-app__tools {
  visibility: hidden;
}
